// External imports
import React, { useState } from "react";
import {
  Box,
  useTheme,
  Stack,
  Avatar,
  Typography,
  Tabs,
  Tab,
  useMediaQuery,
  Button,
  ButtonBase,
  IconButton,
  alpha,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ShareIcon from "@mui/icons-material/Share";
import EditIcon from "@mui/icons-material/Edit";

// Internal imports
import ListMenu from "../../components/ListMenu.js";
import useApi from "../../hooks/useApi.js";
import { useAuth } from "../../context/authContext.js";
import { updateFollowStatusInCache } from "../../utils/cacheUtils.js";
import { shareLink } from "../../utils/share.js";
import { SHARE_ORIGIN } from "../../utils/detailPaths.js";
import HeaderMenuButton from "../../components/HeaderMenuButton.js";
import NetworkDialog from "./NetworkDialog.js";

const AVATAR_SIZE = 90;
// Action buttons (edit / follow, plus share): compact under the name on
// mobile, MUI's medium size at the top right on desktop. One height for the
// text button and the share icon button, so the pair always matches.
const ACTION_SIZES = {
  mobile: { size: "small", height: 28 },
  desktop: { size: "medium", height: 36 },
};

const PAGE_TABS = [
  { value: "dashboard", label: "Dashboard" },
  { value: "profile", label: "Profile" },
];

/**
 * Profile header: avatar, name, bio, follower counts, the profile/follow
 * action and — on your own profile only — the Dashboard/Profile tabs. Scrolls
 * with the page; the content type pill bar below it is what stays sticky.
 *
 * Two layouts, switching at md. Desktop: bio and counts sit under the name;
 * the action button is pinned to the right edge above the tabs. Mobile: the
 * action sits under the name, counts follow the bio, and tabs span the width.
 */
const ProfileHeader = ({
  toggleDrawer,
  activeTab,
  onTabChange,
  profile,
  isOwnProfile,
}) => {
  const { user, openAuthDialog } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { followUser, unfollowUser } = useApi();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openNetworkDialog, setOpenNetworkDialog] = useState(false);
  const [activeNetworkTab, setActiveNetworkTab] = useState("follower");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const action = isMobile ? ACTION_SIZES.mobile : ACTION_SIZES.desktop;
  const isFollowing = profile?.isFollowing || false;

  const followMutation = useMutation({
    mutationFn: followUser,
    onSuccess: (_, username) => {
      updateFollowStatusInCache({
        queryClient,
        currentUser: user,
        username,
        followStatus: true,
      });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: unfollowUser,
    onSuccess: (_, username) => {
      updateFollowStatusInCache({
        queryClient,
        currentUser: user,
        username,
        followStatus: false,
      });
    },
  });

  // The Following button's menu (unfollow only)
  const handleMenuSelect = (value) => () => {
    if (value === "unfollow") {
      unfollowMutation.mutate(profile.username);
    }
    setMenuAnchorEl(null);
  };

  // Shares the profile's URL via the OS share sheet, falling back to copying
  // it to the clipboard.
  const handleShare = () => {
    shareLink({
      url: `${SHARE_ORIGIN}/u/${profile.username}`,
      title: `@${profile.username} on Curate`,
      copiedMessage: `Link copied! Paste it anywhere to share ${
        isOwnProfile ? "your profile" : "this profile"
      }`,
    });
  };

  const handleFollowClick = () => {
    if (!user) {
      openAuthDialog("Following users");
    } else {
      followMutation.mutate(profile.username);
    }
  };

  const openNetwork = (tab) => () => {
    setActiveNetworkTab(tab);
    setOpenNetworkDialog(true);
  };

  const menuOptions = [
    { label: "Unfollow", value: "unfollow", icon: <PersonRemoveIcon /> },
  ];

  const followerCount = profile.followers?.length ?? 0;
  const followingCount = profile.following?.length ?? 0;

  // Rendered under the name on desktop and under the bio on mobile.
  const networkCounts = (
    <Stack direction="row" alignItems="center" spacing={1}>
      <ButtonBase onClick={openNetwork("follower")}>
        <Typography variant="body2" color="text.secondary">
          <b>{followerCount}</b>{" "}
          {followerCount === 1 ? "follower" : "followers"}
        </Typography>
      </ButtonBase>
      <Typography variant="body2" color="text.secondary">
        ·
      </Typography>
      <ButtonBase onClick={openNetwork("following")}>
        <Typography variant="body2" color="text.secondary">
          <b>{followingCount}</b> following
        </Typography>
      </ButtonBase>
    </Stack>
  );

  // Owner: edit; visitor: follow / following (with unfollow in its menu)
  const primaryButton = isOwnProfile ? (
    <Button
      variant="outlined"
      size={action.size}
      onClick={() => navigate("/settings/profile")}
      startIcon={<EditIcon />}
      sx={{ height: action.height }}
    >
      Edit profile
    </Button>
  ) : isFollowing ? (
    <Button
      variant="outlined"
      size={action.size}
      onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      startIcon={<CheckIcon />}
      endIcon={<ExpandMoreIcon />}
      disabled={unfollowMutation.isPending}
      sx={{ height: action.height }}
    >
      Following
    </Button>
  ) : (
    <Button
      variant="contained"
      size={action.size}
      onClick={handleFollowClick}
      disabled={followMutation.isPending}
      startIcon={<PersonAddIcon />}
      sx={{ height: action.height }}
    >
      Follow
    </Button>
  );

  // Share sits beside it for everyone, styled to match the outlined buttons
  const actionButton = (
    <Stack direction="row" spacing={1} alignItems="center">
      {primaryButton}
      <IconButton
        aria-label={isOwnProfile ? "Share your profile" : "Share this profile"}
        onClick={handleShare}
        color="primary"
        size={action.size}
        sx={(theme) => ({
          width: action.height,
          height: action.height,
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        })}
      >
        <ShareIcon fontSize={action.size} />
      </IconButton>
    </Stack>
  );

  const nameLabel = (
    <Typography>
      <b>{profile.givenName}</b> <i>@{profile.username}</i>
    </Typography>
  );

  const bioText = profile.bio && (
    <Typography
      variant="body2"
      // ~2 lines for the 150-char bio limit on wide screens
      sx={{ maxWidth: 500, overflowWrap: "anywhere" }}
    >
      {profile.bio}
    </Typography>
  );

  return (
    <>
      {/* Stacked on mobile; side by side from md, with the tabs column sized
          to its content and the profile column taking the rest */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          pt: 3,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/* Profile column */}
        <Box sx={{ flex: 1, minWidth: 0, pb: 1.5 }}>
          {/* Top-aligned on desktop so a multi-line bio doesn't drag the
              avatar down */}
          <Stack
            direction="row"
            alignItems={{ xs: "center", md: "flex-start" }}
            spacing={2}
          >
            {/* Scrolls away with the header; the pill bar shows its own copy
                once stuck, so the button is always top left */}
            {toggleDrawer && <HeaderMenuButton onClick={toggleDrawer} />}
            <Avatar
              alt={profile.givenName}
              src={profile.avatar_url}
              imgProps={{ referrerPolicy: "no-referrer" }}
              loading="lazy"
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                bgcolor: "tertiary.main",
              }}
            />
            {isMobile ? (
              <Stack spacing={1} alignItems="flex-start">
                {nameLabel}
                {actionButton}
              </Stack>
            ) : (
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                {nameLabel}
                {bioText}
                {networkCounts}
              </Stack>
            )}
          </Stack>

          {/* Mobile: bio and counts drop below the avatar row */}
          {isMobile && (
            <>
              {bioText && <Box sx={{ mt: 1.5 }}>{bioText}</Box>}
              <Box sx={{ mt: 1 }}>{networkCounts}</Box>
            </>
          )}
        </Box>

        {/* Action + tabs column: tabs on the divider, button centred in the
            space above them. Its height is driven by the profile column, so
            the tabs sit beside the bio rather than below it. */}
        <Stack alignItems="flex-end" spacing={1}>
          {!isMobile && (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
              {actionButton}
            </Box>
          )}
          {isOwnProfile && (
            <Tabs
              value={activeTab}
              onChange={(event, value) => onTabChange(value)}
              aria-label="Profile pages"
              sx={{
                width: { xs: "100%", md: "auto" },
                "& .MuiTab-root": { textTransform: "none" },
                [theme.breakpoints.down("md")]: {
                  // Equal-width tabs spanning the screen
                  "& .MuiTab-root": { flex: 1 },
                },
              }}
            >
              {PAGE_TABS.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
          )}
        </Stack>
      </Box>

      {menuAnchorEl && (
        <ListMenu
          anchorEl={menuAnchorEl}
          onClose={() => setMenuAnchorEl(null)}
          onSelect={handleMenuSelect}
          options={menuOptions}
        />
      )}

      <NetworkDialog
        open={openNetworkDialog}
        onClose={() => setOpenNetworkDialog(false)}
        tabValue={activeNetworkTab}
        setTabValue={setActiveNetworkTab}
        profile={profile}
        isOwnProfile={isOwnProfile}
      />
    </>
  );
};

export default ProfileHeader;
